const normalizeDescription = (value) =>
    typeof value === 'string' && value.trim() ? value.trim().toLowerCase() : null

/**
 * Parses the single `c_gscAttributionExtra` JSON attribute (holds the creator
 * region, matching the existing `c_gscFailedItems` JSON-in-a-text-attribute
 * pattern rather than one custom attribute per field). Malformed or missing JSON degrades to `{}`.
 * @param {string|null|undefined} rawValue - The raw `c_gscAttributionExtra` value.
 * @returns {Object}
 */
const parseAttributionExtraField = (rawValue) => {
    try {
        const parsed = rawValue ? JSON.parse(rawValue) : {}
        return parsed && typeof parsed === 'object' ? parsed : {}
    } catch (error) {
        return {}
    }
}

/**
 * Removes `gscToken` from the current URL via a history replace, leaving every
 * other query parameter (including any UTMs), the path, and the hash
 * untouched. A no-op off the server or when the token is already absent.
 *
 * Does not catch its own errors; GTM-facing callers use
 * {@link isUrlSafeForAnalytics} instead, which does.
 *
 * @returns {void}
 */
const stripGscTokenFromUrl = () => {
    if (typeof window === 'undefined') return
    const url = new URL(window.location.href)
    if (!url.searchParams.has('gscToken')) return
    url.searchParams.delete('gscToken')
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
}

const hasGscTokenInUrl = () =>
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('gscToken')

/**
 * Guard for anything that hands the current URL to GTM/GA4. A URL without
 * `gscToken` returns `true` straight away, so ordinary traffic keeps its
 * original GTM path. When the token is present, it is removed synchronously
 * first; if it is still there afterwards (for example `history.replaceState`
 * threw), this returns `false` and the caller must not send, so the opaque
 * token can never reach GA4.
 * @returns {boolean} Whether it is safe to send to GTM now.
 */
const isUrlSafeForAnalytics = () => {
    if (!hasGscTokenInUrl()) return true
    try {
        stripGscTokenFromUrl()
    } catch (error) {
        // Fall through to the re-check below.
    }
    return !hasGscTokenInUrl()
}

/**
 * The live `gscEnabled<Brand>Web` site preference, mirrored here so a plain
 * function (`getGscEventFields`, called from many non-hook contexts: gtm-utils,
 * the Adyen dropin, the giftcard component, the checkout provider, the
 * confirmation page) can read it without itself being a hook.
 *
 * Starts `false` and is only ever `true` once the live preference has been
 * read as on. "Not loaded yet" and "off" are treated the same: an event sent
 * while the value is unknown goes out on time without `gsc_*` fields. Nothing
 * waits for this value and nothing is resent once it arrives, so turning the
 * preference off can never be overridden by a stale or cached "on".
 * @type {boolean}
 */
let gscFeatureEnabled = false

/**
 * Updates the shared "is GSC enabled right now" flag that {@link getGscEventFields}
 * reads. Called by `useGscFeatureFlagSync` whenever the live site preference
 * changes; never call this directly from application code outside that hook.
 * @param {boolean} enabled
 * @returns {void}
 */
const setGscFeatureEnabled = (enabled) => {
    gscFeatureEnabled = Boolean(enabled)
}

/**
 * Whether a basket or order carries a complete GSC attribution, independent of
 * the feature flag. Used to decide whether the flag needs to be read at all,
 * and whether UK purchase dedup applies (see {@link markGscPurchaseIfNew}).
 * @param {Object|null|undefined} basketOrOrder
 * @returns {boolean}
 */
const hasGscAttribution = (basketOrOrder) =>
    Boolean(basketOrOrder?.c_gscAmbassadorId && basketOrOrder?.c_gscReferralSource)

/**
 * GSC fields belong at the event root; the standard items stay untouched.
 *
 * `extraOverride` lets a caller supply the creator-region value from elsewhere
 * instead of `basketOrOrder.c_gscAttributionExtra`. The order a `purchase` event reads no longer carries that attribute (the basket
 * it came from does), so `sendPurchaseEvent` passes the values bridged through
 * {@link consumeGscPurchaseFields} instead. Every other event still reads the
 * basket directly and leaves this unset.
 *
 * Safe to call with a `null`/`undefined` `basketOrOrder` (returns `{}` immediately,
 * the eligibility gate fails closed). Also returns `{}` whenever the GSC site
 * preference is currently off (see {@link setGscFeatureEnabled}) — this is what
 * stops an already-attributed basket or order from continuing to add `gsc_*`
 * fields once the feature flag is turned off mid-session, since this check runs
 * independently of whether `basketOrOrder` itself still carries attribution.
 *
 * This flag check only ever *removes* fields a caller might otherwise expect —
 * it must never be used to decide anything else (purchase dedup, for example,
 * reads {@link hasGscAttribution} directly instead, precisely so dedup doesn't
 * silently turn off along with the flag).
 *
 * @param {Object|null|undefined} basketOrOrder - The basket or order to read the
 *   eligibility gate (`c_gscAmbassadorId`/`c_gscReferralSource`) from.
 * @param {Object|null|undefined} [extraOverride] - Already-parsed creator-region
 *   values, bypassing `basketOrOrder.c_gscAttributionExtra`.
 * @returns {Object} `{}` for ordinary (non-GSC) traffic or whenever the GSC
 *   feature flag is off. Otherwise boolean `gsc_attributed: true` plus whichever of
 *   `gsc_referral_source`/`gsc_creator_region`/`gsc_affiliate_id` are present.
 *   The original UTMs are deliberately not sent in any event.
 */
const getGscEventFields = (basketOrOrder, extraOverride) => {
    if (!gscFeatureEnabled) return {}
    if (!hasGscAttribution(basketOrOrder)) return {}

    const result = {gsc_attributed: true}
    const referralSource = normalizeDescription(basketOrOrder.c_gscReferralSource)
    if (referralSource) result.gsc_referral_source = referralSource

    const extra = extraOverride || parseAttributionExtraField(basketOrOrder.c_gscAttributionExtra)
    const creatorRegion = normalizeDescription(extra.creatorRegion)
    if (creatorRegion) result.gsc_creator_region = creatorRegion

    // The ambassador ID stays in the dataLayer exactly as issued. Keeping it out of
    // GA4 requests is done in GTM, not here.
    result.gsc_affiliate_id = basketOrOrder.c_gscAmbassadorId
    return result
}

const GSC_PURCHASE_FIELDS_KEY = 'gscPurchaseFields'

/**
 * Captures a basket's `c_gscAttributionExtra` into `sessionStorage` right before
 * checkout, so the `purchase` event (fired once the basket has been converted
 * into an order, which carries no such attribute) can still include creator
 * region. Mirrors the existing `purchaseEventParams` sessionStorage bridge in
 * `gtm-utils.js`. Best-effort:
 * a storage failure here only means `purchase` loses this enrichment, never
 * that checkout breaks.
 *
 * DELIBERATE DESIGN DEVIATION from a literal reading of GSC-530 ("persist
 * creator region... through... order creation"):
 * this data is bridged through sessionStorage rather than persisted as an
 * Order custom attribute. Decided explicitly, not a gap — see
 * `gscResolveHelpers.js`'s header and the GSC-530 thread for the reasoning
 * (no non-GA4 consumer for this data exists, unlike the pre-existing
 * `gscAmbassadorId`/`gscReferralSource`, which do stay on Order and remain
 * the purchase event's actual eligibility gate).
 *
 * PAYMENT REDIRECT VALIDATION (code-level, not live-QA'd): checked every
 * redirect-capable payment path in this codebase — `dropin.jsx`'s Adyen
 * `returnUrl` flow (bank/3DS redirects) and the PayPal/Apple Pay express
 * buttons — and none of them call `window.open` or use `target="_blank"`
 * anywhere; every return path (including Adyen's `returnUrl`) lands back on
 * this same origin via a same-tab navigation. `sessionStorage` survives a
 * same-tab navigation away and back, even cross-origin and even across a
 * hard page reload, so this stash correctly survives all of them.
 *
 * SAME-TAB LIMITATION (the trade-off this design accepts): sessionStorage
 * is scoped to one browser tab and is never shared with a different tab,
 * even for the same shopper, same order, same origin. If a real-world
 * payment journey ever completes in a different tab than the one that
 * started checkout — most plausible for a mobile banking app's 3DS
 * app-to-app handoff opening the return URL in a new browser tab/instance
 * instead of reusing the original one — the stashed values are not visible
 * there. The purchase event still fires correctly in that case: the actual
 * eligibility gate (`gsc_attributed`/`gsc_affiliate_id`/`gsc_referral_source`)
 * reads straight off the completed order, unaffected by this. Only the
 * descriptive `gsc_creator_region` enrichment would be missing.
 *
 * Only written for a GSC-attributed basket, so the key's presence also tells
 * the confirmation page (via {@link hasGscPurchaseStash}) that this tab's
 * checkout was attributed, before the order itself has loaded. A non-GSC
 * checkout clears any leftover key instead of writing one.
 *
 * @param {Object|null|undefined} basket
 * @returns {void}
 */
const stashGscPurchaseFields = (basket) => {
    if (typeof window === 'undefined') return
    try {
        if (!hasGscAttribution(basket)) {
            window.sessionStorage.removeItem(GSC_PURCHASE_FIELDS_KEY)
            return
        }
        window.sessionStorage.setItem(
            GSC_PURCHASE_FIELDS_KEY,
            JSON.stringify(parseAttributionExtraField(basket.c_gscAttributionExtra))
        )
    } catch (error) {
        // Best effort; see function doc.
    }
}

/**
 * Whether this tab began checkout with a GSC-attributed basket whose purchase
 * fields have not been consumed yet. Synchronous, so the confirmation page can
 * start reading the feature flag on its first render, alongside the order fetch.
 * @returns {boolean}
 */
const hasGscPurchaseStash = () => {
    if (typeof window === 'undefined') return false
    try {
        return window.sessionStorage.getItem(GSC_PURCHASE_FIELDS_KEY) !== null
    } catch (error) {
        return false
    }
}

/**
 * Reads and clears the fields {@link stashGscPurchaseFields} stashed, for
 * `sendPurchaseEvent` to pass as `getGscEventFields`'s `extraOverride`. Always
 * clears the key, even when attribution is absent, so a stale value from an
 * earlier abandoned GSC checkout can never leak onto an unrelated later order
 * in the same tab.
 * @returns {Object} Parsed fields, or `{}` when absent/unreadable.
 */
const consumeGscPurchaseFields = () => {
    if (typeof window === 'undefined') return {}
    try {
        const raw = window.sessionStorage.getItem(GSC_PURCHASE_FIELDS_KEY)
        window.sessionStorage.removeItem(GSC_PURCHASE_FIELDS_KEY)
        return raw ? JSON.parse(raw) : {}
    } catch (error) {
        return {}
    }
}

/**
 * The UK purchase path (`locales/en-GB/utils/gtm-utils.js`) has no existing
 * duplicate-send guard, unlike the main site's `getTrackedPurchaseOrders`/
 * `trackPurchaseOrder` (AD-628) — so a GSC order could otherwise get a second
 * `purchase` event if the confirmation page re-renders or is reopened. This
 * applies the same `gtmPurchaseSentOrders`-in-`localStorage` dedup, scoped to
 * GSC-attributed orders only, so ordinary (non-GSC) UK purchases are
 * deliberately left exactly as they were before this ticket — this function
 * always returns `true` for them, never touching storage.
 *
 * Gated by {@link hasGscAttribution}, not `getGscEventFields`/the feature
 * flag: whether this order needs dedup is a question about whether it *was*
 * attributed, not about whether the flag happens to be on right now.
 * Otherwise turning the flag off would also silently turn off purchase
 * dedup for already-attributed UK orders, letting a repeat confirmation-page
 * visit resend `purchase` — a real duplicate-event regression, not a
 * `gsc_*`-fields-only change, with no connection to what the flag is meant
 * to control.
 *
 * @param {{orderNo?: string, c_gscAmbassadorId?: string, c_gscReferralSource?: string}|null|undefined} order
 * @returns {boolean} `true` when the caller should proceed and send `purchase`
 *   (an ordinary order, a GSC order with no `orderNo` to dedup by, or a GSC
 *   order not seen before — now recorded); `false` when this GSC order's
 *   `orderNo` was already recorded, so the caller must skip sending.
 */
const markGscPurchaseIfNew = (order) => {
    if (!hasGscAttribution(order)) return true
    if (!order?.orderNo) return true
    try {
        const key = 'gtmPurchaseSentOrders'
        const stored = JSON.parse(window.localStorage.getItem(key) || '[]')
        const orders = Array.isArray(stored) ? stored : []
        if (orders.includes(order.orderNo)) return false
        window.localStorage.setItem(key, JSON.stringify([...orders, order.orderNo].slice(-20)))
    } catch (error) {
        // Keep the existing purchase path working when browser storage is unavailable.
    }
    return true
}

export {
    stripGscTokenFromUrl,
    isUrlSafeForAnalytics,
    getGscEventFields,
    hasGscAttribution,
    markGscPurchaseIfNew,
    stashGscPurchaseFields,
    hasGscPurchaseStash,
    consumeGscPurchaseFields,
    setGscFeatureEnabled
}
